import { uniqueId } from "lodash";
import { FulfillmentUnitStatus, MerchandisePurchaseItem, Purchase } from "types";
import { LotOperation } from "@/hooks/resources/useAdjustInventoryLot";
import {
    getFulfilledQuantity,
    getMerchandisePurchaseItems,
    getMerchandiseWareLineItems,
    getPurchasedWareLineItems,
} from "@/models/MerchandisePurchaseItem";

interface FulfillFulfillmentUnitOp {
    id: string;
    path: string;
    op: "fulfillFulfillmentUnit";
    data: {
        unit: { id: ID };
        quantity: number;
    };
}

export type UpdateFulfillmentPayload = {
    errorStrategy: "continue_on_failure";
    ops: FulfillFulfillmentUnitOp[];
} | null;

type FulfillmentOperationErrorsByLineItemId = Record<string, string[]>;
const FULFILLMENT_OPERATION_ID_SEPARATOR = "::";

interface FulfillmentOperationNode {
    id?: unknown;
    status?: unknown;
    reason?: unknown;
    message?: unknown;
    error?: {
        field?: unknown;
        reason?: unknown;
        message?: unknown;
    };
    [key: string]: unknown;
}

interface FulfillmentOperationError {
    response?: {
        data?: {
            field?: {
                ops?: FulfillmentOperationNode[];
            };
        };
    };
}

const getFulfillmentOperationErrorMessage = (message?: unknown): string => {
    if (typeof message === "string" && message.length > 0) {
        return message;
    }

    return "Failed to apply fulfillment update";
};

const getLineItemIdFromOperationId = (operationId: string): string => {
    const separatorIndex = operationId.lastIndexOf(FULFILLMENT_OPERATION_ID_SEPARATOR);
    if (separatorIndex === -1) {
        return operationId;
    }

    return operationId.slice(0, separatorIndex);
};

const getPendingUnitsByTemplate = (
    fulfillmentUnits: NonNullable<MerchandisePurchaseItem["fulfillmentUnits"]>,
): Record<string, NonNullable<MerchandisePurchaseItem["fulfillmentUnits"]>> => {
    const availableForFulfillment: Record<string, NonNullable<MerchandisePurchaseItem["fulfillmentUnits"]>> = {};

    for (const unit of fulfillmentUnits) {
        const templateId = unit.template?.id;
        if (!templateId || unit.status === FulfillmentUnitStatus.FULFILLED) {
            continue;
        }

        if (!availableForFulfillment[templateId]) {
            availableForFulfillment[templateId] = [];
        }

        availableForFulfillment[templateId].push(unit);
    }

    return availableForFulfillment;
};

export const getFulfillmentOperationErrorsByLineItemId = (
    error: FulfillmentOperationError,
): {
    errorByLineItemId: FulfillmentOperationErrorsByLineItemId;
    hasFulfillmentOperationFailure: boolean;
    successLineItemIds: string[];
} => {
    const mapped: FulfillmentOperationErrorsByLineItemId = {};
    const successLineItemIdSet = new Set<string>();
    let hasFulfillmentOperationFailure = false;

    const operationEntries = error?.response?.data?.field?.ops ?? [];

    for (const operationEntry of operationEntries) {
        const operationId = typeof operationEntry.id === "string" ? operationEntry.id : undefined;
        const lineItemId = operationId ? getLineItemIdFromOperationId(operationId) : undefined;
        if (!lineItemId) {
            continue;
        }

        const operationStatus = typeof operationEntry.status === "string" ? operationEntry.status : undefined;

        if (operationStatus === "success") {
            successLineItemIdSet.add(lineItemId);
            continue;
        }

        const reason = operationEntry.error?.reason ?? operationEntry.reason;
        const messageOrReason = operationEntry.error?.message ?? operationEntry.message ?? reason;
        const message = getFulfillmentOperationErrorMessage(messageOrReason);

        if (!mapped[lineItemId]) {
            mapped[lineItemId] = [message];
        } else if (!mapped[lineItemId].includes(message)) {
            mapped[lineItemId].push(message);
        }
        hasFulfillmentOperationFailure = true;
    }

    return {
        errorByLineItemId: mapped,
        hasFulfillmentOperationFailure,
        successLineItemIds: [...successLineItemIdSet],
    };
};

export const getUpdateFulfillmentPayload = (
    purchase: Purchase,
    toFulfillMap: Record<string, number>,
): UpdateFulfillmentPayload => {
    const merchandiseItems = getMerchandisePurchaseItems(purchase);
    const ops: FulfillFulfillmentUnitOp[] = [];

    for (const item of merchandiseItems) {
        const wareLineItems = getPurchasedWareLineItems(item);
        const fulfillmentUnits = item.fulfillmentUnits ?? [];
        const availableForFulfillment = getPendingUnitsByTemplate(fulfillmentUnits);

        // Match line items with available fulfillment units
        for (const lineItem of wareLineItems) {
            const templateId = lineItem.template?.id;
            if (!templateId) {
                continue;
            }

            const toFulfillCount = toFulfillMap[lineItem.id] ?? 0;
            if (toFulfillCount <= 0) {
                continue;
            }

            const availableUnits = availableForFulfillment[templateId];
            if (!availableUnits?.length) {
                continue;
            }

            // Reduce the available units for this template
            const unitsToMark = availableUnits.splice(0, toFulfillCount);
            let operationIndex = 0;
            for (const unit of unitsToMark) {
                const unitId = unit.unit?.id;
                if (!unitId) {
                    continue;
                }

                ops.push({
                    id: `${lineItem.id}${FULFILLMENT_OPERATION_ID_SEPARATOR}${operationIndex++}`,
                    path: `/items/${item.id}/fulfillmentUnits`,
                    op: "fulfillFulfillmentUnit",
                    data: {
                        unit: { id: unitId },
                        quantity: 1,
                    },
                });
            }
        }
    }

    return ops.length > 0
        ? {
              errorStrategy: "continue_on_failure",
              ops,
          }
        : null;
};

export const getInitialDamageMap = (purchase: Purchase): Record<string, number> => {
    const merchandiseItems = getMerchandisePurchaseItems(purchase);
    const initialMap: Record<string, number> = {};
    for (const item of merchandiseItems) {
        for (const lineItem of getMerchandiseWareLineItems(item)) {
            initialMap[lineItem.id] = 0;
        }
    }
    return initialMap;
};

export const getReportDamagePayload = (
    purchase: Purchase,
    damagedMap: Record<string, number>,
    reason: string,
    templateToLotId: Record<string, string>,
    comment?: string,
): { ops: LotOperation[]; opToLineItem: Record<string, string> } | null => {
    const merchandiseItems = getMerchandisePurchaseItems(purchase);
    const ops: LotOperation[] = [];
    const opToLineItem: Record<string, string> = {};

    for (const item of merchandiseItems) {
        for (const lineItem of getMerchandiseWareLineItems(item)) {
            const templateId = lineItem.template?.id;
            const count = damagedMap[lineItem.id] ?? 0;
            if (count <= 0 || !templateId) continue;

            const lotId = templateToLotId[templateId];
            if (!lotId) continue;

            const opId = uniqueId("damage-op-");
            opToLineItem[opId] = lineItem.id;
            ops.push({
                id: opId,
                op: "increment",
                data: { id: lotId, field: "unavailable", value: count, reason, comment },
            });
        }
    }

    return ops.length > 0 ? { ops, opToLineItem } : null;
};

export const getInitialVariantFulfillMap = (purchase: Purchase) => {
    const merchandiseItems = getMerchandisePurchaseItems(purchase);
    const initialMap: Record<string, number> = {};

    for (const item of merchandiseItems) {
        for (const lineItem of getPurchasedWareLineItems(item)) {
            const fulfilled = getFulfilledQuantity(item, lineItem.template?.id);
            initialMap[lineItem.id] = Math.max(0, (lineItem.quantity ?? 0) - fulfilled);
        }
    }

    return initialMap;
};
